/**
 * The decisions the BORICA return flow has to make, as pure functions.
 *
 * They live here rather than inside OrderDetailsComponent for the reason
 * supplement-pricing.ts does: they are the parts that can be wrong in a way that
 * costs someone money, and a component with fifteen injected services is not a
 * place where that can be tested cheaply.
 *
 * ⚠️ **RC IS A STRING.** BORICA's response code is alphanumeric — a soft-declined
 * VISA answers '1A', and `(int) '1A'` is `1`, which is a different code entirely.
 * Nothing in this file may parse, compare or coerce it as a number.
 */

export type PaymentStatus =
  | 'pending'
  | 'success'
  | 'declined'
  | 'error'
  | 'reversed'
  | 'expired';

export interface PaymentAlert {
  /** Key under `orderDetails.payment.` in the locale files. */
  key: string;
  params?: { rc: string };
}

/**
 * BORICA RC for a form the guest cancelled ("Back to shop"). The spec's wording:
 * "Transaction confirmation state was canceled by user."
 *
 * ⚠️ Trimmed and compared as a STRING, never parsed. `Number('-25')` happens to work;
 * that is not a licence to use it. RC is alphanumeric — a soft-declined VISA answers
 * '1A' — and the moment one code in this file goes through Number() the next one
 * silently becomes something else.
 */
export const USER_CANCELLED_RC = '-25';

/**
 * Whether the guest cancelled BORICA's form themselves.
 *
 * `status=error` AND RC -25. Definite: the card was not charged. A generic `error`
 * without this code is still unknown and must NOT take this path — that is the
 * difference between "we know your card was not charged" and "we do not know", and
 * it is the whole reason this predicate is narrow.
 */
export function isUserCancelledPayment(
  status: string | null | undefined,
  rc: string | null | undefined
): boolean {
  return status === 'error' && String(rc ?? '').trim() === USER_CANCELLED_RC;
}

/**
 * What to tell the guest about a payment that did not succeed.
 *
 * The distinction that matters is between "we know your card was not charged" and
 * "we do not know". A decline, an expiry and a form the guest cancelled are all
 * definite: no capture happened, and saying so is a kindness. Everything else —
 * BoricaOutcome's ERROR catch-all, a status we do not recognise, no answer at all —
 * is NOT definite, and promising an uncharged card there would be a lie about
 * someone's money. Those say we could not confirm it and to ask the venue before
 * paying again.
 */
export function paymentAlert(
  status: string | null | undefined,
  rc: string | null | undefined
): PaymentAlert {
  if (status === 'expired') {
    return { key: 'expired' };
  }

  if (isUserCancelledPayment(status, rc)) {
    return { key: 'cancelled' };
  }

  if (status === 'declined') {
    // ⚠️ String(), never Number(). The code is shown so the guest can read it back
    // to the venue or their bank, and '1A' has to arrive as '1A'.
    //
    // Trimmed, so a code that is nothing but whitespace falls back to the plain
    // decline rather than rendering as "(Code:   )".
    const code = rc === null || rc === undefined ? '' : String(rc).trim();

    return code === ''
      ? { key: 'declined' }
      : { key: 'declinedWithCode', params: { rc: code } };
  }

  return { key: 'unconfirmed' };
}

/**
 * Whether to ask /payment-status again.
 *
 * Pending is a normal state on arrival, not a failure: BORICA's server-to-server
 * callback and the guest's own browser race each other back and the browser
 * usually wins. Only 'pending' is worth another round trip — every other status is
 * final, and re-asking would just delay the answer.
 */
export function shouldRetryPaymentStatus(
  status: string | null | undefined,
  attempt: number,
  maxAttempts: number
): boolean {
  return status === 'pending' && attempt < maxAttempts;
}

/**
 * Whether a failed payment may be attempted again.
 *
 * A decline, or a form the guest cancelled (BORICA RC -25). Both leave the order held
 * server-side and still payable — `borica:check-pending` never touches an order whose
 * transaction reached a final `declined`, and a cancelled form never captured.
 *
 * Nothing else qualifies. An expired session is gone. And an outcome we could not
 * classify must NOT invite a second attempt: we cannot say whether the first one took
 * the money, and offering a retry there is how a guest pays twice.
 */
export function isRetryablePayment(
  status: string | null | undefined,
  rc: string | null = null
): boolean {
  return status === 'declined' || isUserCancelledPayment(status, rc);
}

/**
 * Which message a failed `/pay` deserves, given the HTTP status it failed with.
 *
 * 409 is the backend refusing deliberately — the order is already paid, or no longer
 * awaiting payment because the abandoned-order sweep cancelled it. 404 means it is
 * gone. Neither improves by trying again, and answering "we could not start the
 * payment, please try again" would walk the guest into a loop against a server that
 * has already made up its mind.
 */
export function initiationFailureKey(httpStatus: number | null | undefined): string {
  return httpStatus === 409 || httpStatus === 404 ? 'noLongerPayable' : 'initiationFailed';
}

/**
 * The params BORICA's return URL is allowed to hand on to the redirect target.
 *
 * An allow-list. `id` and `redirect` belong to SplashComponent itself and mean
 * nothing downstream, and forwarding whatever else is in the URL would turn the
 * boot address into a way to push arbitrary state into any screen.
 *
 * This exists because navigateByUrl('/' + redirect) silently dropped ALL of them,
 * so the order screen used to arrive with no idea a payment had just happened.
 */
export function carriedPaymentParams(source: Record<string, unknown> | null | undefined): Record<string, string> {
  const carried: Record<string, string> = {};

  if (!source) {
    return carried;
  }

  (['payment', 'order'] as const).forEach(key => {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') {
      carried[key] = String(value);
    }
  });

  return carried;
}

/**
 * The order id from a returned URL, or null if there is not a usable one.
 *
 * ⚠️ Every one of these params is USER-CONTROLLED — anyone can type
 * `?payment=success&order=1`. This only decides whether an id is worth ASKING the
 * backend about; the answer about the money comes from /payment-status, never from
 * the URL. Rejecting junk here just avoids a pointless request.
 */
export function parseReturnedOrderId(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const orderId = Number(value);

  return Number.isInteger(orderId) && orderId > 0 ? orderId : null;
}

// ────────────────────────────────────────────────────────────────────────────
// The outstanding-payment panel
//
// Everything below serves the screen a guest sees when a card payment did not
// simply succeed. The rule the whole thing is built on:
//
//   ⚠️ ONLY THE BACKEND MAY DECLARE A PAYMENT DEAD.
//      Never the local clock, and never an HTTP 404.
//
// A 404 from /payment-status means "no transaction row exists" — which is the
// `/pay` never started case, an order still fully payable. A 404 from /pay means
// resolveOrder() could not READ the order, and WoocommerceModel::getOrder()
// catches every exception, so a brief WordPress hiccup is indistinguishable from
// a deleted order. And a wall clock cannot know whether BORICA approved anything.
// Treating any of the three as "dead" strands a payable order or buries a real
// payment.
// ────────────────────────────────────────────────────────────────────────────

/** What the outstanding-payment panel should show. */
export type PaymentPhase =
  | 'paid'
  | 'confirming'
  | 'retryable'
  | 'unpayable'
  | 'reversed'
  | 'unconfirmed';

/**
 * The panel phase for a /payment-status answer.
 *
 * ⚠️ Status (and, for `error`, RC) in, phase out — THE CLOCK IS NOT AN INPUT. Which
 * buttons appear is a separate question, answered by the window helpers below; that
 * split is what keeps "the backend decides whether it is dead" from quietly becoming
 * "the browser decides".
 *
 * 'reversed' is deliberately its own phase rather than falling into 'unconfirmed'.
 * A reversal means the card WAS charged and then refunded, and telling that guest
 * "we could not confirm whether your card was charged" is simply false.
 *
 * A form the guest cancelled (`error` + RC -25) is 'retryable', like a decline:
 * definite, uncharged, and still payable. A generic `error` stays 'unconfirmed'.
 *
 * ⚠️ `rc` takes a DEFAULT rather than being optional, so `paymentPhase.length` stays
 * 1 and the arity guard below keeps meaning what it says. RC is not a clock.
 */
export function paymentPhase(
  status: string | null | undefined,
  rc: string | null = null
): PaymentPhase {
  if (isUserCancelledPayment(status, rc)) {
    return 'retryable';
  }

  switch (status) {
    case 'success':
      return 'paid';
    case 'pending':
      return 'confirming';
    case 'declined':
      return 'retryable';
    case 'expired':
      return 'unpayable';
    case 'reversed':
      return 'reversed';
    default:
      // 'error' and anything we do not recognise. NOT payable, and NOT retryable:
      // we cannot say whether the first attempt took the money.
      return 'unconfirmed';
  }
}

/** BoricaPaymentController::initiate() reuses a pending transaction for 15 minutes. */
export const RESUME_WINDOW_MS = 15 * 60 * 1000;

/** borica:check-pending cancels the order behind a decline after 60 minutes. */
export const DECLINE_PAYABLE_MS = 60 * 60 * 1000;

/**
 * Whether calling /pay again would RESUME the existing transaction rather than mint
 * a second one.
 *
 * ⚠️ `txCreatedAt` is the TRANSACTION's created_at, echoed back by /pay — never the
 * time we last called it. The backend measures this window from the row, and a resume
 * returns the ORIGINAL row, so a client timing from its own last call drifts ahead of
 * the server: resume at minute 14, ask again at minute 28, and initiate() no longer
 * matches — a SECOND live transaction is created for one order, with two payable forms
 * outstanding. That is a double charge waiting to happen.
 *
 * Inside this window, "continue payment" is safe and hands back the same form. Outside
 * it, nothing may call /pay while a transaction is still pending.
 */
export function withinResumeWindow(
  txCreatedAt: number | null | undefined,
  now: number
): boolean {
  if (txCreatedAt === null || txCreatedAt === undefined || !Number.isFinite(txCreatedAt)) {
    return false;
  }

  const age = now - txCreatedAt;

  // A negative age means clock skew between device and server, not a fresh transaction.
  // Refusing is the safe direction: the guest waits rather than risking a second sale.
  return age >= 0 && age < RESUME_WINDOW_MS;
}

/**
 * Whether the order behind a DECLINED payment is still payable.
 *
 * The sweep cancels it 60 minutes after the payment attempt. Offering "pay again" past
 * that only earns a 409 rendered as "this order is no longer awaiting payment — speak to
 * the venue", which reads as an accusation for something the system did on a timer.
 * Say the window while it is open, then say the order was released.
 *
 * ⚠️ Measured from the TRANSACTION's created_at, not from when we observed the decline —
 * the same rule `withinResumeWindow` above documents, and for the same reason. The sweep
 * selects on `created_at <= now - 60min` (BoricaCheckPendingTransactions::cancelDeclinedOrders),
 * so timing from the decline drifts past the server: a guest declined at minute 12 would
 * be offered "pay again" until minute 72, twelve minutes after the order was released.
 * The button would be there and could only ever 409.
 *
 * `declinedAt` is still recorded, but it marks THAT a decline happened, not WHEN the
 * clock started.
 *
 * ⚠️ Takes the RECORD, not a timestamp, on purpose. The two fields are both
 * `number | undefined`, so a signature taking a bare number accepts either one happily
 * and the compiler cannot tell them apart — which is exactly how `declinedAt` came to be
 * passed here in the first place. Handing the whole record in means the choice is made
 * once, here, where it is documented.
 */
export function isDeclineStillPayable(
  record: { txCreatedAt?: number | null; declinedAt?: number | null } | null | undefined,
  now: number
): boolean {
  const txCreatedAt = record?.txCreatedAt;

  if (txCreatedAt === null || txCreatedAt === undefined || !Number.isFinite(txCreatedAt)) {
    return false;
  }

  const age = now - txCreatedAt;

  return age >= 0 && age < DECLINE_PAYABLE_MS;
}

/**
 * Whether a /pay that ERRORED may be retried.
 *
 * ⚠️ NOT because "no transaction exists" — that reasoning is false and was the first
 * draft's justification. /pay persists the transaction row BEFORE it returns, so a
 * timeout, a 502 after commit, or a response lost on the wire all leave a real pending
 * transaction behind. What actually makes a retry safe is the 15-minute reuse window
 * above: inside it, initiate() hands back the same row instead of creating another.
 *
 * Terminal, and NOT retryable:
 *   409 — the backend refusing on purpose. Read the `code` to tell the two apart:
 *         'already_paid' means the card WAS charged; 'not_awaiting_payment' means the
 *         order moved on. They are the same HTTP status and opposite facts.
 *   404 — order not found. NOTE: this does not license deleting the local record; see
 *         the header note. It only means this attempt cannot proceed now.
 *   400/403 — misconfiguration (wrong currency) or a venue that cannot take cards.
 *         Retrying can only fail identically, so these get their own copy rather than
 *         "please try again".
 *
 * Retryable: a transport failure (Angular reports status 0 for offline/CORS) and any
 * 5xx, both of which may well succeed on a second attempt.
 */
export function isRetryableInitiation(httpStatus: number | null | undefined): boolean {
  if (httpStatus === null || httpStatus === undefined) {
    return false;
  }

  return httpStatus === 0 || httpStatus >= 500;
}

/**
 * The `code` in the backend's two 409 bodies.
 *
 * ⚠️ They are the same HTTP status and OPPOSITE facts: one says the card WAS charged,
 * the other says the order was never payable. `BoricaPaymentController::initiate()`
 * says so in its own comment — clients must branch on this, never on the
 * human-readable message.
 */
export type InitiationRefusal = 'already_paid' | 'not_awaiting_payment';

/** The refusal a 409 body names, or null when it names none we recognise. */
export function parseInitiationRefusal(code: unknown): InitiationRefusal | null {
  return code === 'already_paid' || code === 'not_awaiting_payment' ? code : null;
}

/**
 * Which message an `unpayable` outcome deserves.
 *
 * A 409 that turned out to be `not_awaiting_payment` is a different sentence from an
 * expired session: the order moved on, rather than the payment window closing.
 */
export function expiredMessageKey(refusal: InitiationRefusal | null | undefined): string {
  return refusal === 'not_awaiting_payment' ? 'noLongerPayable' : 'expired';
}

/** What the panel should show, and whether the answer is the backend's last word. */
export interface PanelOutcome {
  phase: PaymentPhase;
  messageKey: string;
  /** Whether to record this as terminal and stop re-polling on later boots. */
  settle: boolean;
}

/**
 * How many consecutive 404s from /payment-status, across separate boots, before this
 * gives up and tells the guest to speak to the venue.
 *
 * ⚠️ A terminus is required. A 404 is not permission to delete the record — it usually
 * means no transaction row exists, which is an order still fully payable — but without
 * somewhere for the count to end, a permanently-404ing backend re-polls forever and the
 * guest is never told anything at all.
 */
export const NOT_FOUND_TERMINUS = 3;

/**
 * What a 404 from /payment-status means, given how many we have now seen in a row.
 *
 * ⚠️ This is NOT "the order is gone", and nothing here deletes anything. A 404 means no
 * transaction row exists — which is precisely the `/pay`-never-started case, an order
 * still fully payable and the case this whole feature exists to recover. (It is also the
 * answer for an unknown shop, which is why it is counted rather than acted on at once.)
 *
 * ⚠️ The terminus key is `serviceUnreachable`, the panel's BODY — never `contactVenue`,
 * which is the call button's LABEL ("Call the venue"). Passing the label here rendered
 * the whole explanation as those three words, in a phase that shows no call button
 * either, so the guest was told nothing at all.
 */
export function notFoundOutcome(
  record: { txCreatedAt?: number | null } | null | undefined,
  seen: number,
  now: number
): PanelOutcome {
  if (seen >= NOT_FOUND_TERMINUS) {
    return { phase: 'unpayable', messageKey: 'serviceUnreachable', settle: true };
  }

  const txCreatedAt = record?.txCreatedAt;

  // No transaction, or one a fresh /pay would still resume: the order is held and
  // payable, so let them start it. Otherwise a transaction may still be live, and
  // offering a retry against it is how a guest ends up with two payable forms.
  if (txCreatedAt === null || txCreatedAt === undefined || withinResumeWindow(txCreatedAt, now)) {
    return { phase: 'retryable', messageKey: 'initiationFailed', settle: false };
  }

  return { phase: 'confirming', messageKey: 'stillProcessing', settle: false };
}

/**
 * What no answer at all means.
 *
 * NOT a failure of the payment, and nothing may be settled or deleted on it. The record
 * survives untouched and the next boot asks again.
 *
 * A 409 we could not follow up on is the one case worth naming: we know the backend
 * refused, we just do not know which of the two refusals it was — and the one that
 * matters means the card WAS charged.
 */
export function unreachableOutcome(
  refusal: InitiationRefusal | null | undefined,
  txCreatedAt: number | null | undefined
): PanelOutcome {
  if (refusal) {
    return { phase: 'unconfirmed', messageKey: 'unconfirmed', settle: false };
  }

  // No transaction was ever started, so there is nothing to double up on and another
  // attempt is safe. Saying "still being confirmed" here would be describing a payment
  // that never began.
  if (txCreatedAt === null || txCreatedAt === undefined) {
    return { phase: 'retryable', messageKey: 'initiationFailed', settle: false };
  }

  return { phase: 'confirming', messageKey: 'stillProcessing', settle: false };
}

/**
 * What to store about the transaction after a /pay answered.
 *
 * ⚠️ The whole of the `resumed` distinction lives here. A resume hands back the ORIGINAL
 * row, so overwriting the timestamp would let this client's windows drift past the
 * server's — resume at minute 14, re-arm for another 15, and at minute 28 `initiate()`
 * no longer matches and mints a SECOND live transaction for one order. But a genuine
 * retry does create a new row whose `created_at` legitimately restarts every clock, and
 * treating that one as a resume would under-report the window instead.
 *
 * ⚠️ There is NO `Date.now()` fallback. A missing `transaction_created_at` means missing;
 * inventing one arms both windows off a clock the server never saw.
 *
 * Returns `{}` when nothing should change — callers must not patch an empty object,
 * because the store's patch is a spread and `{ txCreatedAt: undefined }` would ERASE a
 * good timestamp rather than leave it alone.
 */
export function rememberedTransaction(
  stored: { txCreatedAt?: number | null } | null | undefined,
  res: {
    transaction_created_at?: string | null;
    transaction_id?: number | null;
    resumed?: boolean | null;
  } | null | undefined
): { txCreatedAt?: number; transactionId?: number } {
  const parsed = res?.transaction_created_at ? Date.parse(res.transaction_created_at) : NaN;
  const created = Number.isFinite(parsed) ? parsed : undefined;

  const fields: { txCreatedAt?: number; transactionId?: number } = {};

  // Absent `resumed` means "we cannot tell", and the safe reading is that this may be a
  // new row — so the timestamp is taken at face value rather than discarded.
  const resumed = res?.resumed ?? false;

  const isFirstSighting = stored?.txCreatedAt === null || stored?.txCreatedAt === undefined;

  if (!resumed && created !== undefined) {
    fields.txCreatedAt = created;
  } else if (isFirstSighting && created !== undefined) {
    // First time we have heard of this transaction at all.
    fields.txCreatedAt = created;
  }

  if (fields.txCreatedAt !== undefined && res?.transaction_id !== null && res?.transaction_id !== undefined) {
    fields.transactionId = res.transaction_id;
  }

  return fields;
}
